import { Alert, Autocomplete, Box, Button, Chip, FormControl, IconButton, InputAdornment, LinearProgress, Paper, Snackbar, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { AccountCircle, AutoFixHigh, Business, CopyAll, EventBusy, EventRepeat, Group, LockReset, OpenInNew, PersonAdd, Pin, Replay, SaveAlt, ShoppingCartCheckout, WorkHistory } from "@mui/icons-material";
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React, { useCallback, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { useNavigate, useParams } from "react-router";
import APIRequest, { APIError, BuildAPIURL } from "system/APIRequest";
import { bytesToString, idToTitle, integerFromTextInput, numberFromTextInput, randomPassword, useQueryParam } from "system/Utilities";
import i18n from "translation";
import { ResourceActionButtonGroup, ResourceActionIcon } from "./ResourceActionButtonGroup";
import * as _ from "lodash";
import { Link } from "react-router-dom";
import { FilterableResourceTable } from "./FilterableResourceTable";
import { TableFilterState } from "./TableFilter";
import { InviteDialog } from "./InviteDialog";
import { permissionTest } from "system/InfegyUser";
import ButtonMenu from "./ButtonMenu";

// Used for pretty printing relative date strings
dayjs.extend(relativeTime);

export interface ResourceEditorPageProps {
  asAdmin?: boolean,
  onBack?: () => void;
}

export default function ResourceEditorPage(props: ResourceEditorPageProps) {
  let navigate = useNavigate();
  let { endpoint, resourceId } = useParams<any>();
  let [_subResource, _setSubResource] = useQueryParam<string | undefined>("sub");
  const [fromId] = useQueryParam<any>("from", null);

  let isCreating = resourceId === ":create";
  if (isCreating)
    resourceId = undefined;

  const [isLoading, setIsLoading] = useState(true);
  const [resource, setResource] = useState<{ [key: string]: any } | undefined>();
  const [edited, setEdited] = useState<{ [key: string]: any }>({});
  const [fetchError, setFetchError] = useState<APIError | undefined>();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<APIError | undefined>();
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "warning" | "error">("success");
  const [additionalSaveParams, setAdditionalSaveParams] = useState<any>({});
  const [userEditedFields, setUserEditedFields] = useState<{ [key: string]: boolean }>({});
  const [shareStatus, setShareStatus] = useState<any>({});  // Used to record special state for shares (deleting, to disable the button, or deleted to hide)
  const [selectedSubResource, setSelectedSubResource] = useState<any>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState<boolean>(false);
  const [passwordResetEnabled, setPasswordResetEnabled] = useState<boolean>(true);

  // This will be set to errors on Revert - set when creating a new resource
  const [initialErrors, setInitialErrors] = useState<{ [key: string]: string }>({});

  const loadResource = useCallback(async () => {
    setIsLoading(true);

    let opts: { [key: string]: string; } = {
      "schema": "1"
    };

    if (props.asAdmin && (!isCreating || fromId)) {
      opts["admin"] = "1";
    }
    
    let uri = `obj/${endpoint}`;

    if (isCreating && !fromId) {
      opts["schema_only"] = "1";
    } else {
      uri += "/" + (resourceId || fromId);
      opts["include_deleted"] = "1";
    }

    await APIRequest(uri, opts, undefined, true).then((response: any) => {
      if (!isCreating || fromId) {
        setEdited(_.cloneDeep(response.output));
      } else {
        // Set object to default values in schema, or null if none
        let defaultValue: { [key: string]: any } = {}
        let editedValue: { [key: string]: any } = {}
        let _errors: { [key: string]: string } = {}
        for (const [key, value] of Object.entries(response.schema as { [key: string]: any; })) {
          editedValue[key] = value.default;
          if (editedValue[key] && !value.readonly) {
            defaultValue[key] = editedValue[key];
          }

          if (!editedValue[key] && !value.readonly) {
            if (value.password_validation) {
              editedValue[key] = randomPassword();
            } else if (value.type === "datetime" || value.type === "date") {
              if (!value.allows_null || value.default !== null) {
                editedValue[key] = new Date().toISOString();
                if (value.type === "date")
                  editedValue[key] = editedValue[key].slice(0, 10); // Trim off time portion
              }
            }
          }

          if (editedValue[key] === undefined && !value.allows_null && !value.readonly) {
            if (value.type === "boolean") {
              // Pretend this defaults to false
              editedValue[key] = false;
            } else {
              // No default, value is required; label as such
              // TODO: i18n
              _errors[key] = 'Required';
            }
          }
        }

        setErrors(_errors);
        setInitialErrors({ ..._errors });
        setEdited(editedValue);
        response.output = _.cloneDeep(defaultValue)
      }

      setResource(response);

    }).catch((reason: APIError) => {
      setResource(undefined);
      setEdited({});
      setFetchError(reason);
    }).finally(() => setIsLoading(false));
  }, [endpoint, resourceId, props.asAdmin, isCreating, fromId]);

  useEffect(() => {
    if (_subResource) {
      setSelectedSubResource(_subResource);
    } else setSelectedSubResource(null);
  }, [_subResource]);

  useEffect(() => {
    // Load data on initial page load
    loadResource();
  }, [loadResource]);

  const saveChanges = () => {
    if (Object.keys(errors).some((key: string) => shouldShowField(key)))
      return;

    let body = generateDiff();
    if (Object.keys(body).length > 0) {
      let opts: { [key: string]: string; } = {};
      if (props.asAdmin) {
        opts["admin"] = "1";
      }

      setIsSaving(true);
      setSnackbarMessage("");
      setSaveError(undefined);

      let uri = `obj/${endpoint}`;
      let method = "POST";

      if (!isCreating) {
        uri += '/' + resourceId;
        method = "PUT";
      }

      // Apply additional params
      for (const [key, value] of Object.entries(additionalSaveParams as { [key: string]: any; })) {
        body[key] = value;
      }

      APIRequest(uri, opts, { "method": method, "body": JSON.stringify(body) }).then((value: any) => {
        if (isCreating) {
          // On a successful create, the output is an object with the new resource's ID
          // Move to that editor now.
          setAdditionalSaveParams({});
          navigate(`/admin/resources/${endpoint}/${value.id}`, { replace: true });
        } else {
          // Apply diffs
          if (resource !== undefined) {
            resource.output = _.cloneDeep(edited);
            setResource({ ...resource });
          }
        }
        setSnackbarMessage(i18n.t("resource-editor.save-successful"));
        setSnackbarSeverity("success");
      }).catch((error: APIError) => {
        setSaveError(error);

        // Server may return a nice error response that tells us where the problem is
        // If so, mark the field with the error
        if (typeof error.body === "object") {
          if (error.body.hasOwnProperty("location") && error.server_message) {
            errors[error.body["location"]] = error.server_message;
          }
        }
      }).finally(() => {
        setIsSaving(false);
      });
    }
  }

  const snackbar = <Snackbar open={snackbarMessage !== ""}
    autoHideDuration={6000}
    onClose={(e: any, reason: any) => setSnackbarMessage("")} >
    <Alert
      onClose={(e: any) => setSnackbarMessage("")}
      severity={snackbarSeverity}
      sx={{ width: '100%' }} >
      {snackbarMessage}
    </Alert>
  </Snackbar>

  if (isLoading) {
    return <div style={{ padding: "2rem 1rem 0 1rem" }}>
      <LinearProgress />
      <p>{i18n.t("loading")}...</p>
      {snackbar}
    </div>;
  } else if (fetchError !== undefined) {
    return <Alert severity="error" style={{ marginTop: "2rem" }}>
      <h3>{i18n.t("admin.resources.load-error-header")}</h3>
      <p>{fetchError?.message || fetchError?.server_message} ({fetchError?.code})</p>
    </Alert>
  } else if (resource === undefined) {
    // No resource, no error, and not loading? what...?
    return <p>No data?</p>
  }

  // Here we display the resource info
  let titleEl: JSX.Element | undefined;
  let descriptionEl: JSX.Element | undefined;

  const editable = (resource.permissions as string[]).includes("update");

  const shouldShowField = (key: string): boolean => {
    if (key === "billing_order_id" || key === "trial_order_id" || key === "parent_account_id" || key == "billing_invoice_id")
      return true;
    
    if (key === "id" || key.endsWith("_id") || key.startsWith("_"))
      return false;

    // This field controls account-level access, which we control via the sharing API
    if (key === "access")
      return false;

    // Data caches need to remain writable, but we do not want users to see nor edit them
    if (key === "cache")
      return false;

    // We display this over the ID for a parent account
    if (key === "parent_account_name")
      return false;
    
    if (key === "deleted_at" && !resource?.output?._deleted)
      return false;

    if (isCreating && resource?.schema[key]?.readonly)
      return false;

    return true;
  }

  const shouldShowGroup = (name: string) => {
    return true;
  }

  const generateDiff = (): { [key: string]: any } => {
    let diff: { [key: string]: any } = {}

    // If we're using create-from, we want to set the same hidden user and/or account ownership fields
    if (isCreating && fromId) {
      if (edited['_user']) {
        diff['user_id'] = edited['_user']['id'];
      }
      if (edited['_account']) {
        diff['account_id'] = edited['_account']['id'];
      }
    }

    for (const key of Object.keys(resource?.schema) as string[]) {
      if (key.startsWith('_')) // Don't include these, they are special generated fields
        continue;

      if (!shouldShowField(key)) // Don't include hidden fields
        continue;
      
      if (resource.schema[key].readonly) // Don't consider readonly fields for editing
        continue;

      // We cannot currently edit JSON fields
      if(resource.schema[key].json)
        continue;

      if (edited[key] === "" && resource.schema[key].allows_null) {
        edited[key] = null;
      }

      if (isCreating) {
        if (edited[key] === false || edited[key] === 0 || edited[key]) {
          diff[key] = edited[key];
        }
      } else {
        if (!_.isEqual(edited[key], resource.output[key])) {
          if (!edited[key] && !resource.output[key] && edited[key] !== 0 && resource.output[key] !== 0 && (edited[key] === null) === (resource.output[key] === null)) {
            continue; // Skip if both are falsey, e.g. was null, we have empty string
          }

          diff[key] = edited[key];
        }
      }
    }

    return diff;
  }

  const setError = (key: string, error?: string): boolean => {
    if (error) {
      errors[key] = error;
      setErrors({ ...errors });
      return true;
    } else if (!error && errors[key]) {
      delete errors[key];
      setErrors({ ...errors });
    }
    return false;
  }

  const validateNumberField = (key: string, value: number | null) => {
    // TODO: i18n
    let err = undefined;
    if (value === null) {
      if (!resource.schema[key].allows_null)
        err = 'Cannot be empty';
    } else if (value > resource.schema[key].max) {
      err = 'Value exceeds maximum of ' + resource.schema[key].max.toString();
    } else if (value < resource.schema[key].min) {
      err = 'Value below minimum of ' + resource.schema[key].min.toString();
    }

    setError(key, err);
  }

  const validateTextField = (key: string, value: string) => {
    const s = resource.schema[key];

    // If this is a password field, and its empty, don't mark as an error
    // This is done as the API will never send back values in the password field
    if (s.password_validation && !value && !isCreating) {
      setError(key, undefined);
      return;
    }

    // TODO: i18n
    let err = !value && !s.allows_null ? 'Cannot be empty' : undefined;
    if (!err && s.validation) {
      if ((value || !s.allows_null) && !RegExp(s.validation).test(value)) {
        err = "Input is not valid";
      }
    }
    if (!err && s.min_length && value.length < s.min_length && (value.length || !s.allows_null)) {
      err = `Must be at least ${s.min_length} characters`;
    }
    if (!err && s.max_length && value.length > s.max_length) {
      err = `Cannot exceed ${s.max_length} characters`;
    }
    if (!err && s.password_validation) {
      let symbols = '~!@#\\$%\\^&\\*\\-\\_=\\+\\.\\?';

      if (!/[a-z]/.test(value)) {
        err = "Must contain at least one lowercase letter";
      } else if (!/[A-Z]/.test(value)) {
        err = "Must contain at least one uppercase letter";
      } else if (!/[0-9]/.test(value)) {
        err = "Must contain at least one number";
      } else if (!RegExp(`[${symbols}]`).test(value)) {
        symbols = symbols.replaceAll("\\", "");
        err = `Must contain at least one symbol: ${symbols}`;
      }
    }

    setError(key, err);
  }

  const handleTextFieldChange = (key: string, value?: string) => {
    value = value || "";

    if (edited[key] !== value) {
      edited[key] = value;

      if (endpoint === "user" && key === "username" && userEditedFields["email"] !== true) {
        edited["email"] = value;
        validateTextField("email", value);
      }

      setEdited({ ...edited });

      validateTextField(key, value);

      userEditedFields[key] = !!value;
      setUserEditedFields({ ...userEditedFields })
    }
  }

  const handleNumberFieldChange = (key: string, value: string) => {
    let val: number | null = null;
    if (resource.schema[key].allows_float) {
      const _val = numberFromTextInput(value);
      if (_val) {
        val = parseFloat(_val);
      }
    } else {
      const _val = integerFromTextInput(value, resource.schema[key].allows_negative);
      if (_val) {
        val = parseInt(_val);
      }
    }

    edited[key] = val;
    setEdited({ ...edited });

    validateNumberField(key, val);
  }

  // TODO: Need better way to detect this - this will trigger after a sucessful create
  /*if (isCreating && endpoint === "user" && !additionalSaveParams.account_id) {
    console.warn('Got to user create page without account_id set');
  }*/

  const diff = generateDiff();
  let fields: { [key: string]: JSX.Element } = {};

  for (const [key, value] of Object.entries(resource.schema as { [key: string]: any; })) {
    if (!shouldShowField(key))
      continue;

    const disabled = !editable || (value.readonly || value.json || false);
    let helperText = errors[key] || (key in diff ? i18n.t("edited") : "");

    // For store fields
    if (!errors[key] && (key.includes('storage') || key.includes('bytes')) && value.type === "number" && edited[key] !== null) {
      helperText = bytesToString(edited[key]) + ' ' + helperText;
    }

    if (value.as === "_title") {
      titleEl = <TextField value={edited[key] || ""}
        key={"titleEl" + key}
        autoComplete="off"
        label={idToTitle(key)}
        disabled={disabled}
        error={Boolean(errors[key])}
        helperText={helperText}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleTextFieldChange(key, e.target.value)} />

    } else if (value.allowed_values) {
      // We need to make sure the value passed to the Autocomplete component valueForComponent.
      // Otherwise we upset the Autocomplete component. And nobody wants an upset Autocomplete.
      let valueForComponent = edited[key];
      if (!valueForComponent)
        valueForComponent = value.default;
      if (valueForComponent === null || valueForComponent === undefined)
        valueForComponent = value.multi_value ? [] : "";

      fields[key] = <FormControl key={"selectEl" + key}>
        <Autocomplete autoComplete autoHighlight disableClearable={!value.multi_value}
          multiple={value.multi_value}
          sx={{ width: value.multi_value ? '40ch' : '20ch', maxWidth: "100%" }}
          size="small"
          renderInput={(params: any) => (<TextField {...params} autoComplete="off" placeholder={valueForComponent.length ? undefined : value.placeholder} variant="standard" />)}
          options={value.allowed_values}
          disabled={disabled}
          value={valueForComponent}
          isOptionEqualToValue={(_opt: any, _val: any) => {
            if (_val.id)
              return _opt.id === _val.id;
            else return _opt.id === _val || _opt.label === _val;
          }}
          onChange={(event: React.SyntheticEvent<Element, Event>, value: string | string[] | null, reason?: string) => {
            edited[key] = value;
            setEdited({ ...edited });

            if (value && errors[key]) {
              delete errors[key];
              setErrors({ ...errors });
            }
          }} />
      </FormControl >
    } else if (value.type === "string") {
      if (key.endsWith("_order_id")) {
        if (edited[key]) {
          fields[key] = <Link to={`/admin/billing/order/${edited[key]}`}>{key.startsWith("trial") ? "Trial Order" : "Active Order"}</Link>
        }
      } else if (key === "parent_account_id") {
        if (edited[key]) {
          fields[key] = <Link to={`/admin/resources/account/${edited[key]}`}>{edited["parent_account_name"]}</Link>
        }
      } else if (key.endsWith("_invoice_id")) {
          if(edited[key]) {
            fields[key] = <Link to={`/admin/billing/invoice/${edited[key]}`}>Invoice</Link>
          }
      } else if (disabled && ((key.includes("_url") || key.includes("_uri")) && edited[key]?.includes('://'))) {
        let linkText = edited[key];
        if (linkText.length > 40) {
          linkText = linkText.slice(0, 40) + "...";
        }
        fields[key] = <a href={edited[key]} target="_blank" rel="noreferrer">{linkText}</a>
      } else {
        const asMultiline = (value.as === "_description" || (value.max_length === undefined || value.max_length > 1024));
        
        let _field = <TextField multiline={asMultiline} rows={asMultiline ? 3 : 1}
          value={value.json ? JSON.stringify(edited[key] || undefined, undefined, 2) : (edited[key] || "")}
          style={{ width: '38ch', maxWidth: "100%" }}
          key={"stringEl" + key}
          autoComplete="off"
          size="small"
          variant={asMultiline ? "outlined" : "standard"}
          disabled={disabled}
          error={Boolean(errors[key])}
          helperText={helperText}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleTextFieldChange(key, e.target.value)} />

        if(key === "hubspot_company") {
          _field = <Box>{_field}<Link target="_blank" to={"https://app.hubspot.com/contacts/1716283/company/" + edited[key]}><OpenInNew /></Link></Box>
        }

        if (!disabled && key === "password") {
          // This is done to prevent the silly Tooltip-on-a-disabled-component warning
          // We will output the disabled button alone, and only wrap in a Tooltip if not disabled
          const copyButton = <IconButton size="small" disabled={!edited[key]} onClick={async () => {
            await navigator.clipboard.writeText(edited[key]);
          }}>
            <CopyAll />
          </IconButton>

          _field = <Grid container spacing={0} sx={{justifyContent: "space-around", maxWidth: 400}} key={"grid" + key}>
            <Grid size={{ xs: 10 }} key={"field-" + key}>
              {_field}
            </Grid>
            <Grid size={{ xs: 2 }} key={key + "-buttons"}>
              <Stack direction="row">
                <Tooltip title={i18n.t("resource-editor.generate-random-" + key) as string} key={"gen-" + key}>
                  <IconButton color="success" size="small" onClick={() => {
                    if (key === "password") {
                      edited[key] = randomPassword();
                    }
                    if (errors[key]) {
                      delete errors[key];
                      setErrors({ ...errors });
                    }
                    setEdited({ ...edited });
                  }}>
                    <AutoFixHigh />
                  </IconButton>
                </Tooltip>
                {edited[key] &&
                  <Tooltip title={i18n.t("resource-editor.copy-" + key) as string} key={"copy-" + key}>
                    {copyButton}
                  </Tooltip>}
                {!edited[key] && copyButton}
              </Stack>
            </Grid>
          </Grid>
        }
        fields[key] = _field;
      }
    } else if (value.type === "boolean") {
      if (value.allows_null) {
        // If the boolean value allows null, we need a null option, so three choices rather than a switch's two
        // For null, if we are working with a user, label as "inherit", as it will inherit the account's permission
        //  If not a user, its just null
        // The weird "null" stuff here is to fix an error with ToggleButton, which doesnt like null as a value
        fields[key] = <ToggleButtonGroup
          color="primary"
          size="small"
          value={edited[key] === null ? "null" : edited[key]}
          exclusive
          onChange={(event: React.MouseEvent<HTMLElement, MouseEvent>, value: any) => {
            edited[key] = value === "null" ? null : value;
            setEdited({ ...edited });
          }}
        >
          <ToggleButton value={"null"} color="standard">{i18n.t(endpoint === "user" ? "inherit" : "null")}</ToggleButton>
          <ToggleButton value={false} color="error">{i18n.t("disable")}</ToggleButton>
          <ToggleButton value={true} color="success">{i18n.t("enable")}</ToggleButton>
        </ToggleButtonGroup>
      } else {
        // Boolean does not allow null, so just an off/on switch
        fields[key] = <Switch checked={edited[key] || false}
          key={"switchEl" + key}
          disabled={disabled}
          onChange={(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
            edited[key] = checked;
            setEdited({ ...edited });
          }} />
      }
    } else if (value.type === "number") {
      fields[key] = <TextField disabled={disabled}
        size="small"
        key={"numEl" + key}
        autoComplete="off"
        sx={{ width: disabled ? '10ch' : '14ch' }}
        error={Boolean(errors[key])}
        helperText={helperText}
        placeholder={value.allows_null && endpoint === "user" ? i18n.t("inherit") : undefined}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleNumberFieldChange(key, e.target.value)}
        variant="standard" 
        slotProps={{
          input: {
            type: disabled ? undefined : "number",
            inputMode: 'numeric',
            startAdornment: disabled ? undefined : <InputAdornment position="start"><Pin /></InputAdornment>
          }
        }}
        value={edited[key] === 0 ? 0 : edited[key] || ""} />

    } else if (value.type === "datetime") {
      if (!resource.output[key] && disabled) {
        if (key !== "scheduled_termination" && key != "trial_termination") {
          fields[key] = <TextField autoComplete="off" variant="standard" sx={{ width: '19ch' }} disabled value={"never"} size="small" key={"dateEl" + key} />
        }
      } else {
        fields[key] = <DateTimePicker disabled={disabled}
          value={edited[key] ? dayjs(edited[key]) : null}
          minDate={dayjs(new Date(2007, 1, 1))}
          slotProps={{
            textField: {
              key: "datetimeEl" + key,
              variant: "standard",
              sx: { width: disabled ? '20ch' : '22ch' },
              error: Boolean(errors[key]),
              helperText: errors[key] || (edited[key] ? dayjs(edited[key]).fromNow() : undefined)
            }
          }}
          
          onChange={(val: any) => {
            if (val && val.toString().startsWith("Invalid")) {
              errors[key] = val.toString();
              setErrors({ ...errors });
            } else {
              handleTextFieldChange(key, typeof val === "object" && val ? val.toISOString() : "");
            }
          }} />
      }
    } else if (value.type === "date") {
      if (!resource.output[key] && disabled) {
        fields[key] = <TextField autoComplete="off" variant="standard" sx={{ width: '21ch' }} disabled value={"never"} size="small" key={"dateEl" + key} />
      } else {
        let localVal: null | Dayjs = null;
        if (edited[key]) {
          const asDate = new Date(edited[key]);
          localVal = dayjs(new Date(asDate.getUTCFullYear(), asDate.getUTCMonth(), asDate.getUTCDate(), 0, 0, 0));
        }

        fields[key] = <DatePicker disabled={disabled}
          value={localVal}
          minDate={dayjs.utc("2007-01-01")}
          slotProps={{
            textField: {
              key: "dateEl" + key,
              variant: "standard",
              sx: { width: disabled ? '19ch' : '22ch' },
              error: Boolean(errors[key]),
              helperText: errors[key] || (edited[key] ? dayjs(edited[key]).fromNow() : undefined)
            }
          }}
          
          onChange={(val: any) => {
            if (val && val.toString().startsWith("Invalid")) {
              errors[key] = val.toString();
              setErrors({ ...errors });
            } else {
              handleTextFieldChange(key, typeof val === "object" && val ? val.toISOString().slice(0, 10) : "");
            }
          }} />
      }
    } else if (key.startsWith("_")) {
      // Ignore these special fields here
      continue;
    } else {
      console.error(`Unknown field type on ResourceEditorPage: ${value}`);
      Sentry.captureMessage(`Unknown field type on ResourceEditorPage: ${value}`);
    }
  }

  const paperStyle = { maxWidth: 700, width: "100%", padding: "1rem", margin: "8 auto" };

  // Enable save if:
  //    we aren't currently saving
  //    at least one field has been changed
  //    any fields shown dont have an error
  const saveEnabled = !isSaving && Object.keys(diff).length > 0 && !Object.keys(errors).some((key: string) => shouldShowField(key));

  // Action buttons appear at the top of the editor
  // This controls what actions will appear
  const actionButtonActions = resource.permissions.filter((v: string) => {
    if (v === "read" || v === "update" || v === "create" || v == "ui_create")
      return false;
    if (v === "restore" && edited._deleted !== true)
      return false;
    if (v === "remove" && edited._deleted === true)
      return false;
    if (v === "unshare" && edited._is_shared !== true)
      return false;
    if (v === "include_deleted")
      return false;
    return true;
  });

  const subResourceFilters: Record<string, TableFilterState> = {};
  if (selectedSubResource && resource?.output) {
    subResourceFilters[selectedSubResource.filter] = {
      id: selectedSubResource.filter,
      op: "=",
      value: resource.output.id
    }
  }
  
  const scheduledTermination = resource?.output?.billing_order_id ? resource?.output?.scheduled_termination : resource?.output?.trial_termination;

  return (
    <div>
      {!isCreating && <div style={{ paddingLeft: "1rem", paddingBottom: "1rem", paddingTop: "0.5rem", display: "flex", gap: "0.5rem", justifyContent: "space-around", maxWidth: 700, flexWrap: "wrap", margin: "0 auto" }}>
        {resource?.output?._deleted && // Show an alert if the resource is deleted, letting the user know
          <Alert style={{ flex: "0 0 100%", marginBottom: "1rem" }} severity="warning">
            {i18n.t("resource-editor.deleted-warning")}
          </Alert>}

        {/* We want dedicated buttons for Accounts on th edit page, so this special-case oddity is here...
            This operation also needs to send the account_id, which we have from here */}
        {endpoint === "account" &&
          <>
            {resource?.output?.billing_order_id && !resource?.output?._deleted &&
              <ButtonMenu variant="contained"
                buttonLabel="New Order"
                startIcon={<ShoppingCartCheckout />}
                menuItems={[
                  {name: 'Run a Trial', id: 'trial'},
                  {name: 'Change Current Order', id: 'change'},
                  {name: 'Renewal Order', id: 'renewal'},
                ]}
                onSelect={(item: { id: string; name: string; }) => {
                  window.open(`/admin/billing/change-order/${resourceId}?type=${item.id}`);
                }}
              />
            }
            {(!resource?.output?.billing_order_id || resource?.output?._deleted) &&
              <Tooltip title="Create an order on this account to activate service or run a trial.">
                <Button variant="contained" color="primary"
                  startIcon={<ShoppingCartCheckout />}
                  href={`/admin/billing/change-order/${resourceId}`}
                >
                  New Order
                </Button>
              </Tooltip>
            }
          </>
        }
        {endpoint === "account" && !resource?.output._deleted && <>
          <Button variant="contained" color="success"
            startIcon={<PersonAdd />}
            onClick={() => {
              setInviteDialogOpen(true);
            }}>
            {i18n.t("invite users")}
          </Button>
          
          {permissionTest("super_admin") && !resource?.output?.scheduled_termination &&
            <Tooltip title={scheduledTermination ? `Service will be cancelled ${dayjs(scheduledTermination).fromNow()}` :
              (resource?.output.billing_order_id ?
                "Schedule termination of service at the end of the active term" :
                "Cancel service immediately")}>
              <div>
                <Button variant="contained" color="error" loading={isSaving}
                  startIcon={<EventBusy />}
                  disabled={!!scheduledTermination || !!resource?.output.deleted_at}
                  onClick ={((event: any) => {
                    event.preventDefault();
                    setIsSaving(true);
                    // Send API request to terminate
                    APIRequest("billing/terminate/" + resourceId, {}, { method: "DELETE" }, true, 1).then(async () => {
                      setSnackbarMessage("Termination request successful");
                      setSnackbarSeverity("success");
                      await loadResource();
                    }).catch(() => {
                      setSnackbarMessage("Termination request failed!");
                      setSnackbarSeverity("error");
                    }).finally(() => {
                      setIsSaving(false);
                    });
                  })}
                >
                  {resource?.output.billing_order_id ? "Schedule Termination" : "Cancel Service"}
                </Button>
              </div>
            </Tooltip>
          }
          {permissionTest("super_admin") && resource?.output?.scheduled_termination &&
            <Tooltip title={`Service will be cancelled ${dayjs(scheduledTermination).fromNow()}. Use this to stop cancellation from occurring.`}>
              <div>
                <Button variant="contained" color="warning" loading={isSaving}
                  startIcon={<EventRepeat />}
                  onClick ={((event: any) => {
                    event.preventDefault();
                    setIsSaving(true);
                    // Send API request to terminate
                    APIRequest("billing/un-schedule-termination/" + resourceId, {}, { method: "POST" }, true, 1).then(async () => {
                      setSnackbarMessage("Termination cancelled successfully");
                      setSnackbarSeverity("success");
                      await loadResource();
                    }).catch(() => {
                      setSnackbarMessage("Un-terminate request failed!");
                      setSnackbarSeverity("error");
                    }).finally(() => {
                      setIsSaving(false);
                    });
                  })}
                >
                  Cancel Termination
                </Button>
              </div>
            </Tooltip>
          }
          
          {!permissionTest("super_admin") &&
            <Tooltip title="Termination of service requires super admin privileges. Please contact a super admin.">
              <Button variant="contained" color="grey"
                startIcon={<EventBusy />}
              >
                Cancel Service
              </Button>
            </Tooltip>
          }
          
          {inviteDialogOpen &&
            <InviteDialog
              open={inviteDialogOpen}
              accountId={resourceId as string}
              onClose={(e: {}, reason: "escapeKeyDown" | "backdropClick") => {
                setInviteDialogOpen(false);
              }}
              handleComplete={(wasSuccessful: boolean, numberInvited: number, error?: APIError | undefined) => {
                if (wasSuccessful) {
                  if(numberInvited == 1) {
                    setSnackbarMessage(i18n.t(`resource-action.dialog.invite-users-single-success`));
                  } else if(numberInvited > 1) {
                    setSnackbarMessage(i18n.t(`resource-action.dialog.invite-users-multi-success`));
                  }
                  setSnackbarSeverity("success");
                } else {
                  setSnackbarMessage("");
                }
                setInviteDialogOpen(false);
              }} />
          }

        </>}

        {/* We want a button to trigger password resets for users */}
        {endpoint === "user" && !resource.output._deleted && <>
          <Button
            variant="contained"
            color="primary"
            startIcon={<LockReset />}
            disabled={!passwordResetEnabled}
            onClick={() => {
              setPasswordResetEnabled(false);
              // Send API request to fire off the invite
              APIRequest("iforgot", {email: resource?.output?.email}, { method: "POST" }).then((value: string) => {
                setSnackbarMessage(i18n.t("resource-editor.password reset sent"));
                setSnackbarSeverity("success");
              }).catch((reason: APIError) => {
                setSnackbarMessage(i18n.t("resource-editor.password reset failed"));
                setSnackbarSeverity("error");
              });
            }}>
            {i18n.t("resource-editor.send password reset")}
          </Button>
          
          {inviteDialogOpen &&
            <InviteDialog
              open={inviteDialogOpen}
              accountId={resourceId as string}
              onClose={(e: {}, reason: "escapeKeyDown" | "backdropClick") => {
                setInviteDialogOpen(false);
              }}
              handleComplete={(wasSuccessful: boolean, numberInvited: number, error?: APIError | undefined) => {
                if (wasSuccessful) {
                  if(numberInvited == 1) {
                    setSnackbarMessage(i18n.t(`resource-action.dialog.invite-users-single-success`));
                  } else if(numberInvited > 1) {
                    setSnackbarMessage(i18n.t(`resource-action.dialog.invite-users-multi-success`));
                  }
                  setSnackbarSeverity("success");
                } else {
                  setSnackbarMessage("");
                }
                setInviteDialogOpen(false);
              }} />
          }

        </>}

        <ResourceActionButtonGroup
          resourcePath={`/admin/resources/${endpoint}`}
          actions={actionButtonActions}
          endpoint={endpoint || ""}
          resourceIds={[resourceId || ""]}
          defaultAccountId={resource?.output?._account?.id}
          accountAccess={resource?.output?.access}
          removeAs={["user", "account"].includes(endpoint || "") ? "deactivate" : "remove"}
          restoreAs={["user", "account"].includes(endpoint || "") ? "reactivate" : "restore"}
          handleActionComplete={async (wasSuccessful: boolean, resourceIds: string[], action: string, error?: APIError | undefined) => {
            if (wasSuccessful) {
              setSnackbarMessage(i18n.t(`resource-action.dialog.${action}-single-success`));
              setSnackbarSeverity("success");
              await loadResource();
            } else {
              setSnackbarMessage("");
            }
          }}
          asAdmin={props.asAdmin}
          showSharedWarning={edited._is_shared === true} />
      </div>}

      <Stack spacing={2} sx={{
        alignItems: "center"
      }}>
        <Paper style={paperStyle} key="titlePaper">
          <Stack spacing={2}>
            <Typography variant="h4">
              {isCreating ? `${i18n.t("new")} ` : ""}{resource?.display_name || idToTitle(endpoint || "")}
            </Typography>
            {titleEl}
            {descriptionEl}
            
            {endpoint === "account" && resource?.output?.trial_order_id &&
              <Alert icon={<WorkHistory fontSize="inherit" />} severity="success">
                {i18n.t("admin.active-trial")}
              </Alert>
            }
          </Stack>
        </Paper>

        {((resource?.output?._user && endpoint !== "user") || (resource?.output?._account && endpoint !== "account")) &&
          <Paper style={paperStyle} key="ownerPaper">

            <Grid container spacing={2}>
              <Grid size={{ xs:12 }} key={"ownerPaperTitle"}>
                <Stack direction="row" spacing={1}>
                  {resource?.output?._user ? <AccountCircle color="primary" /> : <Business color="primary" />}
                  <Typography variant="subtitle1" color="primary">
                    {i18n.t(resource?.output?._user ? "owner" : "account")}
                  </Typography>
                </Stack>
              </Grid>

              {resource?.output?._user &&
                <Grid size={{ xs: 6 }} key={"ownerUser"}>
                  <Link style={{ color: 'inherit', textDecoration: 'inherit' }} to={"/admin/resources/user/" + resource.output._user.id}>
                    <Chip icon={<AccountCircle />} label={resource.output._user.email} color={resource.output._user.active ? "info" : "default"} />
                  </Link>
                </Grid>}

              {resource?.output?._account && endpoint !== "account" && <>
                <Grid size={{ xs: 6 }} key={"ownerAccount"}>
                  <Link style={{ color: 'inherit', textDecoration: 'inherit' }} to={"/admin/resources/account/" + resource.output._account.id}>
                    <Chip icon={<Business />} label={resource.output._account.name} color={resource.output._account.active ? "success" : "default"} />
                  </Link>
                </Grid></>}
            </Grid>
          </Paper>}

        {resource?.output?._shares && resource.output._shares.length > 0 &&
          <Paper style={paperStyle} key="sharesPaper">
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }} key={"ownerPaperTitle"}>
                <Stack direction="row" spacing={1}>
                  <ResourceActionIcon action="share" color="primary" />
                  <Typography variant="subtitle1" color="primary">
                    {i18n.t("shares")}
                  </Typography>
                </Stack>
              </Grid>

              {resource.output._shares.map((share: any, index: number) =>
                shareStatus[share.id]?.deleted ? undefined :
                  <Grid size={{ sm: 4, xs: 6 }} key={share.id}>
                    <Link style={{ color: 'inherit', textDecoration: 'inherit' }} to={"/admin/resources/" + share.type + "/" + share.to_id}>
                      <Chip
                        disabled={shareStatus[share.id]?.deleting}
                        icon={share.type === "account" ? <Business /> : share.type === "team" ? <Group /> : <AccountCircle />}
                        label={share.to_name} color={share.to_is_active ? (share.type === "account" ? "success" : share.type === "team" ? "warning" : "primary") : "default"}
                        onDelete={((event: any) => {
                          event.preventDefault();

                          shareStatus[share.id] = { deleting: true };
                          setShareStatus({ ...shareStatus });

                          // Send API request to delete the share
                          APIRequest("obj/shared-resource/" + share.id, props.asAdmin ? { "admin": "1" } : {}, { method: "DELETE" }).then((value: any) => {
                            setSnackbarMessage(i18n.t("resource-editor.share-delete-success"));
                            setSnackbarSeverity("success");
                            shareStatus[share.id] = { deleted: true };
                            setShareStatus({ ...shareStatus });
                          }).catch((reason: APIError) => {
                            delete shareStatus[share.id];
                            setShareStatus({ ...shareStatus });
                            setSnackbarMessage(i18n.t("resource-editor.share-delete-fail"));
                            setSnackbarSeverity("error");
                          });
                        })}
                      />
                    </Link>
                  </Grid>
              )}
            </Grid>
          </Paper>
        }

        {Object.entries(resource.schema_groups).map((group): JSX.Element | undefined => {
          const [group_name, group_members] = group

          if (!shouldShowGroup(group_name)) {
            return undefined;
          }

          const getGroupEl = (key: string, title: string, el: JSX.Element) => {
            const smallStacked = resource?.schema[key]?.type !== "boolean";

            return <React.Fragment key={"frag-" + key}>
              <Grid size={{ xs: smallStacked ? 12 : 9, sm: 3 }} key={"title-" + key} sx={{textAlign: {xs: 'left', sm: 'right'}}}>
                <Typography variant="h6" color={errors[key] ? "error" : undefined}>
                  {title}
                </Typography>
              </Grid>
              <Grid size={{ xs: smallStacked ? 12 : 3, sm: 9 }} key={"field-" + key} sx={{textAlign: "left"}}>
                {el}
              </Grid>
            </React.Fragment>
          };

          const group_els = (group_members as string[]).map((key: string): JSX.Element | undefined => {
            if (!fields[key])
              return undefined;

            let title = idToTitle(key);
            if (group_name && title.startsWith(group_name) && title !== group_name) {
              title = title.slice(group_name.length);
              title = title.slice(title.indexOf(' ') + 1).trim();
            }
            if (title.endsWith(' Enabled')) {
              title = title.substring(0, title.length - 8);
            }

            return getGroupEl(key, title, fields[key]);
          })

          if (!group_els.some((value: JSX.Element | undefined) => value !== undefined))
            return undefined;

          return <Paper style={paperStyle} key={"paper-" + group_name}>
            <Grid container spacing={2}>
              {group_name &&
                <Grid size={{ xs: 12 }} key={"gi-" + group_name} sx={{textAlign: {xs: 'left'}}}>
                  <Typography variant="h4" color="primary">
                    {group_name}
                  </Typography>
                </Grid>}

              {group_els}
            </Grid>
          </Paper>
        })}

        <Stack
          direction="row"
          key={"editButtons"}
          sx={{
            justifyContent: "space-between",
            width: "100%",
            maxWidth: 700
          }}>
          {editable && <Button
            key="revert-button"
            color="primary"
            variant="contained"
            startIcon={<Replay />}
            disabled={Object.keys(diff).length === 0}
            onClick={() => {
              setEdited(_.cloneDeep(resource.output));
              setErrors({ ...initialErrors });
              setSaveError(undefined);
            }}>
            {i18n.t("revert")}
          </Button>}

          {editable && <Button
            key="save-button"
            color="success"
            variant="contained"
            startIcon={<SaveAlt />}
            disabled={!saveEnabled}
            onClick={() => saveChanges()} >
            {i18n.t("save")}
          </Button>}
        </Stack>

        {isSaving && <LinearProgress />}
        {saveError !== undefined && <Alert severity="error">{saveError.message}<br />{saveError.body?.location ? (idToTitle(saveError.body?.location) + ": " + saveError.server_message) : ""}</Alert>}
      </Stack>

      {resource?.sub_resources?.length > 0 &&
        <Stack
          spacing={2}
          sx={{
            padding: 2,
            alignItems: "center"
          }}>
          <Autocomplete openOnFocus autoComplete autoHighlight
            onChange={(event: object, value: any) => {
              setSelectedSubResource(value || null);
              _setSubResource(value);
            }}
            options={resource.sub_resources}
            value={selectedSubResource}
            getOptionLabel={(option: any) => idToTitle(option.endpoint)}
            style={{ width: 300 }}
            isOptionEqualToValue={(option: any, value: any) => option.endpoint === value.endpoint}
            renderInput={(params: any) => <TextField {...params} autoComplete="off" label={i18n.t("admin.resources.select-resource-placeholder")} variant="outlined" />}
          />

          {selectedSubResource !== null &&
            <FilterableResourceTable
              endpoint={selectedSubResource.endpoint}
              displayName={selectedSubResource.name}
              asAdmin={props.asAdmin}
              allowCreate={false}
              baseFilters={subResourceFilters}
            />}
        </Stack>
      }

      {snackbar}
    </div >
  );
}